import sentryConfig from 'src/store/sentry.js'
import { DEFAULT_SLINK, DISCOVERY_FEATURES } from 'src/config/constants.ts'
import Core from 'src/api/core.js'
import { jwtDecode } from 'jwt-decode'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import { KeycloakAuth } from 'src/config/auth/KeycloakAuth.ts'
import { isKeycloakUser, requiresOidcAuth } from '@/composables/auth/useAuthHelper.ts'
import { AUTH_TYPE } from 'src/config/constants.ts'
import { i18n } from 'src/config/i18n.ts'

const defaultState = () => ({
  users: {},
  useNewLogin: false
})

export const state = defaultState()

export const getters = {
  adminAuthToken(state, getters) {
    return getters.adminUser?.authToken
  },
  adminUser(state) {
    const adminUsers = Object.values(state.users).filter(user => user.school?.slink === DEFAULT_SLINK)
    return adminUsers[0]
  },
  currentStoredUser(state) {
    const storedUsers = Object.values(state.users)
    const uuid = localStorage?.getItem('user_uuid')
    return storedUsers.find(el => el.uuid === uuid)
  },
  storedUsers(state) {
    return state.users
  }
}

/**
 * Saves users to localStorage or removes the entry if users object is empty.
 * @param {Object} users - Users object keyed by user ID
 */
const saveLocalStorage = (users) => {
  if (Object.keys(users).length === 0) {
    return localStorage.removeItem('multiple_users')
  }

  localStorage.setItem('multiple_users', JSON.stringify(users))
}

const isUserSwitching = (targetUserId, currentUser) => {
  return targetUserId !== currentUser?.id
}

export const mutations = {
  /**
   * Resets state to default values.
   */
  RESET_STATE(state) {
    Object.assign(state, defaultState())
  },
  /**
   * Loads multi-user data from localStorage and applies backward compatibility.
   */
  LOAD_MULTI_USERS(state) {
    const storedData = localStorage.getItem('multiple_users') || '{}'
    const users = JSON.parse(storedData)

    if (!users || typeof users !== 'object') {
      sentryConfig.throwError('LOAD_MULTI_USERS() failed to parse multiple_users from localStorage')
      return
    }

    // Backward compatibility: default authType to 'passport' for legacy users
    Object.keys(users).forEach(userId => {
      if (!users[userId].authType) {
        users[userId].authType = AUTH_TYPE.PASSPORT
      }
    })

    state.users = users
  },
  SAVE_USER(state, { user, authType, refreshToken, idToken, realm, keycloakSub }) {
    const { id, firstname, lastname, school, meta, type, uuid } = user

    const existingUser = state.users[id]

    state.users = {
      ...state.users,
      [user.id]: {
        id,
        firstname,
        lastname,
        school,
        meta,
        type,
        uuid,
        authToken: existingUser?.authToken,
        childs: existingUser?.childs || [],
        authType: authType ?? existingUser?.authType,
        refreshToken: refreshToken ?? existingUser?.refreshToken,
        idToken: idToken ?? existingUser?.idToken,
        realm: realm ?? existingUser?.realm,
        keycloakSub: keycloakSub ?? existingUser?.keycloakSub,
      }
    }

    saveLocalStorage(state.users)
  },
  /**
   * Updates the auth token for a specific user.
   */
  SAVE_USER_AUTH_TOKEN(state, { userId, authToken }) {
    const user = state.users[userId]
    if (!user) return
    state.users = {
      ...state.users,
      [userId]: { ...user, authToken }
    }

    saveLocalStorage(state.users)
  },
  SAVE_USER_REFRESH_TOKEN(state, { userId, refreshToken }) {
    const user = state.users[userId]
    if (!user || !isKeycloakUser(user)) return

    state.users = {
      ...state.users,
      [userId]: { ...user, refreshToken }
    }

    saveLocalStorage(state.users)
  },
  /**
   * Saves family (childs) data for a parent user.
   */
  SAVE_FAMILY(state, { childs, userId }) {
    state.users = {
      ...state.users,
      [userId]: { ...state.users[userId], childs }
    }

    saveLocalStorage(state.users)
  },
  /**
   * Removes a user from the multi-user store.
   */
  REMOVE_USER(state, userId) {
    state.users = GlobalPrototypes.clone({
      ...state.users,
      [userId]: undefined
    })

    saveLocalStorage(state.users)
  },
  /**
   * Removes a user by UUID (convenience wrapper).
   */
  REMOVE_USER_BY_UUID(state, userUuid) {
    if (!userUuid) return
    this.commit('multiUsers/REMOVE_USER', Object.values(state.users).find(user => user.uuid === userUuid)?.id)
  },
  /**
   * Changes the useNewLogin flag.
   */
  CHANGE_USE_LOGIN(state, bool) {
    state.useNewLogin = bool
  }
}

export const actions = {
  /**
   * Initializes multi-user module by loading data from localStorage.
   * Executed before the app is initialized (see main.ts).
   */
  init({ commit }) {
    commit('LOAD_MULTI_USERS')

    // Restore useNewLogin flag when returning from Keycloak session clearing redirect.
    // The redirectLoginSign middleware checks useNewLogin before component mount —
    // without this, it would redirect the user away from the login page.
    if (sessionStorage?.getItem('kc-reauth-intent')) {
      commit('CHANGE_USE_LOGIN', true)
    }
  },
  async saveUser({ commit, rootState }, { user, authType, realm, keycloakSub }) {
    if (!user.school) {
      sentryConfig.throwError('user-school-obj-is-undefined-in-multi_users')
    }

    let refreshToken
    let idToken

    // For keycloak users, always read refresh token and id token from oidc-client
    if (authType === AUTH_TYPE.KEYCLOAK && realm) {
      const kcAuth = await KeycloakAuth.getInstance(realm)
      const oidcUser = await kcAuth?.getUser()
      refreshToken = oidcUser?.refresh_token
      idToken = oidcUser?.id_token

      if (!refreshToken || !idToken) {
        sentryConfig.addBreadcrumb({
          category: 'auth',
          message: `saveUser: missing OIDC tokens for keycloak user (refreshToken: ${!!refreshToken}, idToken: ${!!idToken})`,
          level: 'warning'
        })
      }
    }

    commit('SAVE_USER', { user, authType, refreshToken, idToken, realm, keycloakSub })

    try {
      const { sub: authUserId } = jwtDecode(rootState.authToken)
      commit('SAVE_USER_AUTH_TOKEN', { userId: authUserId, authToken: rootState.authToken })
    } catch {
      sentryConfig.throwError('saveUser: failed to decode authToken from rootState')
    }
  },
  /**
   * Saves family (childs) data for a parent user.
   */
  async saveFamily({ commit }, { childs, userId }) {
    commit('SAVE_FAMILY', { childs, userId })
  },
  /**
   * Switches to a different user account.
   * Handles both Keycloak (OIDC refresh) and Passport authentication.
   * If OIDC token is expired, removes the account and shows notification.
   * @param {string} userId - User ID to switch to
   * @param {string} path - Optional redirect path (from push notification)
   */
  async selectUser({ state, commit, dispatch, getters }, { userId, path }) {
    const user = state.users[userId]

    if (!user) {
      throw new Error('User could not be selected.')
    }

    const school = user.school

    // Handle auth type switching
    if (requiresOidcAuth(user)) {
      const kcAuth = await KeycloakAuth.getInstance(user.realm)

      const existingOidcUser = await kcAuth?.getUser()

      const isSameUser = !!existingOidcUser?.profile?.sub && existingOidcUser.profile.sub === user.keycloakSub

      // is the same user and the token is still valid
      if (isSameUser && existingOidcUser && !existingOidcUser.expired) {
        localStorage.setItem('auth_token', existingOidcUser.access_token)

        if (existingOidcUser.refresh_token) {
          commit('SAVE_USER_REFRESH_TOKEN', {
            userId,
            refreshToken: existingOidcUser.refresh_token
          })
        }
      } else {
        if (existingOidcUser?.refresh_token) {
          const existingUserInStore = Object.values(state.users)
            .find(u => u.keycloakSub === existingOidcUser.profile?.sub)

          if (existingUserInStore) {
            commit('SAVE_USER_REFRESH_TOKEN', {
              userId: existingUserInStore.id,
              refreshToken: existingOidcUser.refresh_token
            })
          }
        }

        // if the user has a refresh token, try to refresh. 
        if (user.refreshToken) {
          try {
            const refreshedUser = await kcAuth.restoreUserWithToken(user.refreshToken)

            commit('SAVE_USER_REFRESH_TOKEN', {
              userId,
              refreshToken: refreshedUser.refresh_token
            })

            localStorage.setItem('auth_token', refreshedUser.access_token)
          } catch {
            // if refresh fails, remove the user 
            if (user.realm) {
              await kcAuth.clearStorageForRealm(user.realm)
            }

            commit('REMOVE_USER', userId)

            GlobalPrototypes.toastNotify({
              type: 'warning',
              text: i18n.global.t('global.multi_account.session_expired', { name: `${user.firstname} ${user.lastname}` })
            })

            if (path) return GlobalPrototypes.router.push('/users-select')
            return
          }
        } else {
          // no refresh token, just redirect to login
          await kcAuth.signinRedirect({ prompt: 'login' })
          return
        }
      }
    } else {
      await KeycloakAuth.clearStorage()
      localStorage.setItem('auth_token', user.authToken)
    }

    school && GlobalPrototypes.store.commit('schoolBranding/SET_BRANDING', school)

    await dispatch('SET_SLINK', school.slink, { root: true })
    localStorage?.setItem('slink', school.slink)
    await dispatch('keepAlive/setKeepAlivePing', null, { root: true })

    // don't switch accounts if the push-user is already logged in
    const me = getters.currentStoredUser
    const isSwitching = isUserSwitching(userId, me)
    if (!isSwitching && path) return GlobalPrototypes.router.push(GlobalPrototypes.router.resolve(path))

    // Track account switch - only when switching to a different user
    if (isSwitching) {
      const crossSaleFeatures = []

      // Pupil features - from school.properties
      const properties = me?.school?.properties || {}
      if (!!properties['pupil.room_booking.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.ROOM_BOOKING)
      }
      if (!!properties['pupil.daycare.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.DAY_CARE)
      }
      if (!!properties['pupil.parent_meetings.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.PARENT_MEETINGS)
      }
      if (!!properties['pupil.media_library.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.MEDIA_LIBRARY)
      }
      if (!!properties['pupil.meeting_protocols.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.MEETING_PROTOCOLS)
      }
      if (!!properties['pupil.inventory.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.INVENTORY)
      }
      if (!!properties['pupil.social_management.enabled']) {
        crossSaleFeatures.push(DISCOVERY_FEATURES.SOCIAL_MANAGEMENT)
      }

      // Additio features - from meta.sdui_group.features array
      const sduiGroupFeatures = me?.meta?.sdui_group?.features || []
      const additioFeatures = [DISCOVERY_FEATURES.DIGITAL_GRADE_BOOK, DISCOVERY_FEATURES.LESSON_PLANNING, DISCOVERY_FEATURES.LEARNING_REPORTS]
      additioFeatures.forEach(name => {
        if (sduiGroupFeatures.some(f => f.name === name && f.active)) {
          crossSaleFeatures.push(name)
        }
      })

      // Educa features - from meta.sdui_group.features array
      const educaFeatures = [DISCOVERY_FEATURES.AI_ASSISTANT, DISCOVERY_FEATURES.GRADES_AND_CERTIFICATIONS, DISCOVERY_FEATURES.SEVEN_TIMETABLE]
      educaFeatures.forEach(name => {
        if (sduiGroupFeatures.some(f => f.name === name && f.active)) {
          crossSaleFeatures.push(name)
        }
      })

      clyTracking.trackAuth().action.switchAccounts({
        crossSaleSchool: crossSaleFeatures.length > 0,
        crossSaleFeatures: crossSaleFeatures.join(',') || 'none'
      })
    }

    commit('SET_USER_UUID', user.uuid, { root: true })
    await dispatch('users/resetCacheStorage', null, { root: true })

    // If the current route is timetable (/timetable, but also /no-timetable), we need to redirect to
    // /timetable so the component can decide where to route for the other user
    const isCurrentRouteTimetable = GlobalPrototypes.router.currentRoute.value.path.indexOf('timetable') !== -1
    // if we're not at timetable, redirect to the same page as the user which was logged in before
    const urlAfterUserSwitch = isCurrentRouteTimetable ? 'timetable' : GlobalPrototypes.router.currentRoute.value.path
    // if a path is given we're coming from a click on a push notification. Redirect to this exact path. Otherwise
    // the user used the UserChangeModal
    const url = path ? new URL(path, location.origin) : new URL(urlAfterUserSwitch, location.origin)
    // .. and append the switchedAccounts param so we can display the notification
    url.searchParams.append('switchedAccounts', 'true')
    // also append any existing hashes like #read
    if (location.hash) url.hash = location.hash

    const pathBefore = new URL(path, location.origin)
    if (pathBefore.pathname === urlAfterUserSwitch) return location.replace(url.href)
    location.href = url.href
  },
  /**
   * Removes a user and logs them out from the backend.
   * Handles both Keycloak (token revocation) and Passport logout.
   */
  async removeAndLogoutUser({ dispatch, getters, state }, user) {
    const storedUser = Object.values(getters.storedUsers).find(el => el.uuid === user.uuid)

    if (!storedUser) return

    const isLastUser = Object.keys(state.users).length === 1
    const isOidcUser = requiresOidcAuth(storedUser)

    // if last oidc user, delegate to users/logout for full logout
    if (isOidcUser && isLastUser) {
      await dispatch('users/logout', {
        ...user,
        school: storedUser.school,
        idToken: storedUser.idToken,
        realm: storedUser.realm
      }, { root: true })
      return
    }

    try {
      if (isOidcUser) {
        // No null-guard: if getInstance fails, let it throw into the catch block
        const kcAuth = await KeycloakAuth.getInstance(storedUser.realm)
        await kcAuth.revokeUserSession(
          storedUser.realm,
          storedUser.refreshToken
        )
      } else {
        await Core.postLogout(
          { device_token: GlobalPrototypes.store.state.device.firebaseToken || undefined },
          { showMessages: false, actingAs: user }
        )
      }
    } catch (e) {
      if (!Object.values(getters.storedUsers).find(el => el.uuid === user.uuid)) return
      throw e
    }

    await dispatch('removeUser', user)
  },
  /**
   * Removes a user from the store and switches to another user if needed.
   */
  async removeUser({ state, commit, dispatch, getters }, user) {
    const me = getters.currentStoredUser

    if (!me) {
      sentryConfig.addBreadcrumb({
        category: 'UUID localstorage',
        message: localStorage?.getItem('user_uuid'),
        level: 'warning'
      })
      sentryConfig.throwError('removeUser() failed to fetch currentStoredUser')
    }

    commit('REMOVE_USER_BY_UUID', user?.uuid)

    if (user?.id && (me?.id !== user.id)) return
    if (!Object.keys(state.users).length) {
      return dispatch('users/logoutClearCache', user, { root: true })
    }

    await GlobalPrototypes.sleep(300)
    const nextUserForLogin = Object.keys(state.users)[0]
    await dispatch('selectUser', { userId: nextUserForLogin, path: '/users-select' })
  },
}
